<template>
  <div class="project-offers">
    <p v-if="isNewProject" class="text-muted">{{ $t('saveNewProjectFirst') }}</p>
    <template v-else>
      <div v-if="showOfferForm && editingOffer" class="offer-form-section mb-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              {{ isNewOffer ? $t('offer_create') : $t('offer_edit') }}
              <span v-if="!isNewOffer">
                #{{ editingOffer.number.toString().padStart(4, '0') }}
              </span>
            </h5>
            <button class="btn btn-sm btn-outline-secondary" @click="closeOfferForm">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <div class="card-body">
            <OfferForm
              :offer="editingOffer"
              :projectContext="project"
              :isReadOnly="isBlockReadOnly"
              @save="handleSaveOffer"
              @cancel="closeOfferForm"
            />
          </div>
        </div>
      </div>

      <OffersTable
        :projectNumber="project.number"
        :tableStatePrefix="`project-${project.number}-offers`"
        :showProjectColumn="false"
        :isReadOnly="isBlockReadOnly"
        :canOpenOfferPage="canOpenOfferPage"
        @edit-offer="handleEditOffer"
        @create-offer="createNewOffer"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import OffersTable from '@/components/Offers/OffersTable.vue';
import OfferForm from '@/components/Offers/OfferForm.vue';
import { coversAccessLevel } from '@/helpers/accessLevel';
import { OFFERS_ACCESS_NODE } from '@/navigation/accessNodes';
import { AccessBlockMixin } from '@/mixins/accessBlock';

/** Number of the unsaved project (ProjectEdit `_new`) and of an unsaved offer. */
const NEW_NUMBER = '_new';

/**
 * Block project-offers ("Angebote", see feature page projekt-angebote-rechnungen): the offers of
 * the project through the paths of the block (`projectNumber` in the offer actions). Matrix-only,
 * as the view offers. The table stays usable for viewers (filters and paging); every write control
 * is covered by `isBlockReadOnly`.
 *
 * No `BlockFieldset` root: the content is a table and a form, as the view offers.
 */
export default {
  name: 'ProjectOffers',
  components: {
    OffersTable,
    OfferForm,
  },
  mixins: [AccessBlockMixin],
  props: {
    /** The page project; the offers are loaded by `OffersTable`. */
    project: { type: Object, required: true },
  },
  data() {
    return {
      showOfferForm: false,
      editingOfferNumber: null,
    };
  },
  computed: {
    ...mapGetters(['getOfferByNumber', 'getAccessLevel']),

    isNewProject() {
      return this.project.number === NEW_NUMBER;
    },

    isNewOffer() {
      return this.editingOffer.number === NEW_NUMBER;
    },

    /**
     * The offer number opens the view offers only with a level on it; otherwise the number is
     * plain text.
     */
    canOpenOfferPage() {
      return coversAccessLevel(this.getAccessLevel(OFFERS_ACCESS_NODE), 'viewer');
    },

    /** Always the latest offer from the store. */
    editingOffer() {
      if (!this.editingOfferNumber) return null;
      return this.getOfferByNumber(this.editingOfferNumber);
    },
  },
  methods: {
    ...mapActions(['initOffer', 'createOffer', 'updateOffer', 'fetchOfferByNumber']),

    async handleEditOffer(offer) {
      await this.fetchOfferByNumber({
        offerNumber: offer.number,
        projectNumber: this.project.number,
      });
      this.editingOfferNumber = offer.number;
      this.showOfferForm = true;
    },

    async handleSaveOffer(offer) {
      try {
        if (offer.number === NEW_NUMBER) {
          await this.createOffer({ offer, projectNumber: this.project.number });
        } else {
          await this.updateOffer({ offer, projectNumber: this.project.number });
        }
        this.closeOfferForm();
      } catch (error) {
        // the store action already shows the error message; the form stays open
        console.error('Error saving offer:', error);
      }
    },

    closeOfferForm() {
      this.showOfferForm = false;
      this.editingOfferNumber = null;
    },

    createNewOffer() {
      this.initOffer();
      this.editingOfferNumber = NEW_NUMBER;
      this.showOfferForm = true;
    },
  },
};
</script>

<style scoped lang="scss">
.offer-form-section {
  .card {
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;

    .card-header {
      background-color: #f8f9fa;
      border-bottom: 1px solid #dee2e6;
      padding: 0.75rem 1rem;

      h5 {
        color: #495057;
        font-weight: 600;
      }
    }

    .card-body {
      padding: 1rem;
    }
  }
}

.project-offers {
  .btn-outline-secondary {
    border-color: #6c757d;
    color: #6c757d;

    &:hover {
      background-color: #6c757d;
      border-color: #6c757d;
      color: white;
    }
  }
}
</style>
